import { useNavigate, useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import Input from 'MediaManager/components/Form/Input'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getUnitType } from 'MediaManager/services/property'
import { deleteUnit, upsertUnits } from 'MediaManager/services/units'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'shared/components/Button'
import Loader from 'shared/components/Loader'
import { convertStringNumbersToNumbers } from 'shared/helpers/general'
import { useRequest } from 'shared/hooks/useRequest'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'

function UnitsEditScreen(props) {
  const { line, id } = useSearch()
  const [resultValue, setResultValue] = useState()
  const navigate = useNavigate()

  const getData = useCallback(async () => {
    if (!line) {
      return
    }
    const { data } = await getUnitType(line)
    return data.unit_types[0]
  }, [line])

  const { data, loading } = useRequest(getData)

  const validationSchema = Yup.object().shape({
    floor: Yup.string().required(errorMessages.required),
    unit_number: Yup.string().required(errorMessages.required),
    name: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        await upsertUnits({
          unit_type_id: line,
          ...formik.values,
          floor: Number(formik.values.floor),
          ...(id ? { id } : {}),
        })
        setResultValue('success')
      } catch (error) {
        setResultValue('error')
      }
    },
  })

  const isValid = useMemo(() => {
    return Object.values(formik.errors).every((v) => !v)
  }, [formik])

  function getFieldError(field) {
    return formik.errors[field] && formik.touched[field] ? formik.errors[field] : null
  }

  useEffect(() => {
    if (data && id) {
      const unit = data.units.find((u) => u.id === Number(id))
      formik.setValues({
        building_name: unit.building_name,
        floor: unit.floor,
        unit_number: unit.unit_number,
        name: unit.name,
      })
    }
  }, [data, id])

  async function onDelete() {
    await deleteUnit(Number(id))
    window.history.back()
  }

  return (
    <ScreenLayout title={`${id ? 'Update' : 'Add'} unit`}>
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      {!loading && (
        <>
          {resultValue && (
            <ActionSuccessModal
              action={`Unit`}
              successText="has been successfully upserted."
              errorText="upserting failed. Please try again."
              state={resultValue}
              error={'Please try again later.'}
              skipConfirm
              skipBack
              onClose={() => {
                if (resultValue === 'success') {
                  window.history.back()
                } else {
                  setResultValue(null)
                }
              }}
            />
          )}
          <form onSubmit={formik.handleSubmit} className="w-full">
            <div>
              <Input label="Property" disabled value={data.property.property_name} />
              <Input wrapClassName="mt-[16px]" label="Line" disabled value={data.name} />
              <Input
                wrapClassName="mt-[16px]"
                label="Name"
                name="name"
                onChange={formik.handleChange}
                value={formik.values.name}
                onBlur={formik.handleBlur}
                required
                error={getFieldError('name')}
              />
              <Input
                wrapClassName="mt-[16px]"
                label="Unit number"
                name="unit_number"
                onChange={formik.handleChange}
                value={formik.values.unit_number}
                onBlur={formik.handleBlur}
                required
                error={getFieldError('unit_number')}
              />
              <Input
                wrapClassName="mt-[16px]"
                label="Floor"
                name="floor"
                onChange={formik.handleChange}
                value={formik.values.floor}
                onBlur={formik.handleBlur}
                required
                number
                error={getFieldError('floor')}
              />

              <Input
                wrapClassName="mt-[16px]"
                label="Building"
                name="building_name"
                onChange={formik.handleChange}
                value={formik.values.building_name}
                onBlur={formik.handleBlur}
                error={getFieldError('building_name')}
              />
              <div
                className={`flex justify-end items-center mt-[16px] ${
                  formik.isSubmitting ? 'pointer-events-none  opacity-50' : ''
                }`}
              >
                {id && (
                  <Button rounded variant="secondary" onClick={onDelete} className="mr-[16px]">
                    Delete
                  </Button>
                )}
                <Button rounded variant="secondary" onClick={() => window.history.back()}>
                  Cancel
                </Button>
                <Button loading={formik.isSubmitting} disabled={!isValid} rounded className="ml-[16px]" type="submit">
                  {id ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          </form>
        </>
      )}
    </ScreenLayout>
  )
}

export default UnitsEditScreen
